/**
 * Workspace Context
 *
 * Provides global state management for workspaces using React Context API.
 * Follows the same pattern used in ProjectContext.
 *
 * MULTI-WORKSPACE MODEL:
 * - Users can OWN 1 workspace (workspace.owner_id - unique constraint)
 * - Users can be MEMBERS of N workspaces (via WorkspaceMember)
 * - Users can switch between accessible workspaces (switchWorkspace)
 * - Backend validates access on every API call
 * - TD/superadmins can view ALL workspaces through admin interface
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import {
  Workspace,
  WorkspaceMember,
  WorkspaceGroup,
  WorkspaceGroupMember,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  AddWorkspaceMemberRequest,
  UpdateWorkspaceMemberRequest,
  CreateWorkspaceGroupRequest,
  UpdateWorkspaceGroupRequest,
  AddGroupMemberRequest,
  WorkspaceContextType,
} from '../types/workspace';
import * as workspaceService from '../services/workspaceService';
import { useAuth } from './AuthContext';

// Create context with default values
const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

// Local storage key for active workspace
const ACTIVE_WORKSPACE_KEY = 'activeWorkspaceId';

/**
 * WorkspaceProvider component
 * Wraps the application to provide workspace state and operations
 */
export const WorkspaceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState<boolean>(false);  // Prevent concurrent switches

  /**
   * Load all workspaces for the user
   *
   * MULTI-WORKSPACE MODEL:
   * - Users can OWN 1 workspace (workspace.owner_id)
   * - Users can be MEMBERS of N workspaces (via WorkspaceMember)
   * - Backend returns ALL accessible workspaces (owner + member)
   * - User's owned workspace is ALWAYS first (backend ordering)
   * - User can switch between accessible workspaces
   *
   * SECURITY:
   * - localStorage stores activeWorkspaceId for UX only (restore last active)
   * - Backend validates access on every API call
   * - User cannot access workspace they're not owner/member of
   */
  const loadWorkspaces = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Use getUserWorkspaces to get ALL accessible workspaces
      // For TD/superadmins: Returns ALL workspaces (administrative access)
      // For regular users: Returns workspaces where they are owner/member
      const response = await workspaceService.getUserWorkspaces();
      if (response.success && response.data) {
        // Ensure response.data is always an array
        const workspaceList = Array.isArray(response.data) ? response.data : [];
        setWorkspaces(workspaceList);

        if (workspaceList.length > 0) {
          // Try to restore previously active workspace from localStorage
          const savedWorkspaceId = localStorage.getItem(ACTIVE_WORKSPACE_KEY);

          if (savedWorkspaceId) {
            // SECURITY: Validate that saved workspace belongs to current user
            // This prevents workspace contamination if logout doesn't clear localStorage
            // Only restore workspace if user has access to it
            const savedWorkspace = workspaceList.find(
              w => w.id === parseInt(savedWorkspaceId)
            );

            if (savedWorkspace) {
              // Restore saved workspace (validated access)
              setCurrentWorkspace(savedWorkspace);
              return;
            } else {
              // Saved workspace not accessible by current user - clear it
              localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
            }
          }

          // Default: Use first workspace (user's owned workspace)
          // Backend ensures owned workspace is always first
          const defaultWorkspace = workspaceList[0];
          setCurrentWorkspace(defaultWorkspace);
          localStorage.setItem(ACTIVE_WORKSPACE_KEY, defaultWorkspace.id.toString());
        } else {
          // No workspace found (shouldn't happen in normal flow)
          console.error('User has no workspace - this should not happen. User may need to complete registration.');
          setCurrentWorkspace(null);
          localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
        }
      } else {
        // If no data, set empty array
        setWorkspaces([]);
        setCurrentWorkspace(null);
        localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load workspaces';
      setError(errorMessage);
      setWorkspaces([]); // Ensure it's always an array even on error
      setCurrentWorkspace(null);
      localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
      console.error('Failed to load workspaces:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Create a new workspace
   * Automatically sets it as the active workspace
   *
   * @deprecated No modelo 1:1 User:Workspace, workspaces são criados automaticamente
   * durante o registro. Esta função é mantida para compatibilidade com código legado,
   * mas não deve ser usada por novos recursos.
   */
  const createWorkspace = useCallback(async (data: CreateWorkspaceRequest): Promise<Workspace> => {
    setError(null);

    try {
      const response = await workspaceService.createWorkspace(data);
      if (response.success && response.data) {
        const newWorkspace = response.data;

        // Add to workspaces list
        setWorkspaces(prev => [newWorkspace, ...(Array.isArray(prev) ? prev : [])]);

        // Set as current workspace
        setCurrentWorkspace(newWorkspace);
        localStorage.setItem(ACTIVE_WORKSPACE_KEY, newWorkspace.id.toString());

        return newWorkspace;
      }

      throw new Error('Invalid server response');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create workspace';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Update an existing workspace
   */
  const updateWorkspace = useCallback(
    async (workspaceId: number, data: UpdateWorkspaceRequest): Promise<Workspace> => {
      setError(null);

      try {
        const response = await workspaceService.updateWorkspace(workspaceId, data);
        if (response.success && response.data) {
          const updatedWorkspace = response.data;

          // Update in workspaces list
          setWorkspaces(prev => (Array.isArray(prev) ? prev : []).map(w => (w.id === workspaceId ? updatedWorkspace : w)));

          // Update current workspace if it's the one being updated
          setCurrentWorkspace(prev => (prev?.id === workspaceId ? updatedWorkspace : prev));

          return updatedWorkspace;
        }

        throw new Error('Invalid server response');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update workspace';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  /**
   * Delete a workspace
   */
  const deleteWorkspace = useCallback(
    async (workspaceId: number): Promise<void> => {
      setError(null);

      try {
        const response = await workspaceService.deleteWorkspace(workspaceId);
        if (response.success) {
          // Remove from workspaces list
          setWorkspaces(prev => {
            const prevArray = Array.isArray(prev) ? prev : [];
            const remainingWorkspaces = prevArray.filter(w => w.id !== workspaceId);

            // If deleted workspace was active, switch to another one
            if (currentWorkspace?.id === workspaceId) {
              if (remainingWorkspaces.length > 0) {
                setCurrentWorkspace(remainingWorkspaces[0]);
                localStorage.setItem(ACTIVE_WORKSPACE_KEY, remainingWorkspaces[0].id.toString());
              } else {
                setCurrentWorkspace(null);
                localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
              }
            }

            return remainingWorkspaces;
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete workspace';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [currentWorkspace]
  );

  /**
   * Switch to a different workspace.
   *
   * MULTI-WORKSPACE MODEL:
   * - User can switch between workspaces they have access to
   * - Backend validates access before allowing switch
   * - Updates localStorage for persistence across sessions
   *
   * SECURITY:
   * - Backend endpoint validates user has access (owner/member/TD)
   * - Cannot switch to workspace user doesn't have access to
   *
   * @param workspaceId - Workspace ID to switch to
   */
  const switchWorkspace = useCallback(
    async (workspaceId: number): Promise<void> => {
      // RACE CONDITION PREVENTION: Don't allow concurrent switch operations
      if (isSwitching) {
        console.warn('Workspace switch already in progress, ignoring request');
        return;
      }

      setError(null);
      setIsSwitching(true);

      try {
        // Call backend to validate access
        const response = await workspaceService.switchWorkspace(workspaceId);

        if (response.success && response.data) {
          // Reload workspaces FIRST to get fresh data
          await loadWorkspaces();

          // Then update current workspace (prevents flash of stale data)
          setCurrentWorkspace(response.data);
          localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspaceId.toString());
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to switch workspace';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsSwitching(false);
      }
    },
    [isSwitching, loadWorkspaces]
  );

  /**
   * Set a workspace as the active one (Local only - no backend validation)
   *
   * @deprecated Use switchWorkspace() instead for proper backend validation.
   * This function is kept for backward compatibility.
   */
  const setActiveWorkspace = useCallback(
    async (workspaceId: number | null): Promise<void> => {
      console.warn('setActiveWorkspace is deprecated - use switchWorkspace() instead');
      setError(null);

      try {
        if (workspaceId === null) {
          setCurrentWorkspace(null);
          localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
          return;
        }

        const workspace = workspaces.find(w => w.id === workspaceId);
        if (workspace) {
          setCurrentWorkspace(workspace);
          localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspaceId.toString());
        } else {
          throw new Error('Workspace not found');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to set active workspace';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [workspaces]
  );

  // =====================================================================
  // MEMBER OPERATIONS
  // =====================================================================

  const loadWorkspaceMembers = useCallback(async (workspaceId: number): Promise<WorkspaceMember[]> => {
    setError(null);

    try {
      const response = await workspaceService.getWorkspaceMembers(workspaceId);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Invalid server response');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load members';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const addWorkspaceMember = useCallback(
    async (workspaceId: number, data: AddWorkspaceMemberRequest): Promise<WorkspaceMember> => {
      setError(null);

      try {
        const response = await workspaceService.addWorkspaceMember(workspaceId, data);
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Invalid server response');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add member';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  const updateWorkspaceMember = useCallback(
    async (
      workspaceId: number,
      userId: number,
      data: UpdateWorkspaceMemberRequest
    ): Promise<WorkspaceMember> => {
      setError(null);

      try {
        const response = await workspaceService.updateWorkspaceMember(workspaceId, userId, data);
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Invalid server response');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update member';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  const removeWorkspaceMember = useCallback(async (workspaceId: number, userId: number): Promise<void> => {
    setError(null);

    try {
      const response = await workspaceService.removeWorkspaceMember(workspaceId, userId);
      if (!response.success) {
        throw new Error('Failed to remove member');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove member';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  // =====================================================================
  // GROUP OPERATIONS
  // =====================================================================

  const loadWorkspaceGroups = useCallback(async (workspaceId: number): Promise<WorkspaceGroup[]> => {
    setError(null);

    try {
      const response = await workspaceService.getWorkspaceGroups(workspaceId);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Invalid server response');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load groups';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const createWorkspaceGroup = useCallback(
    async (workspaceId: number, data: CreateWorkspaceGroupRequest): Promise<WorkspaceGroup> => {
      setError(null);

      try {
        const response = await workspaceService.createWorkspaceGroup(workspaceId, data);
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Invalid server response');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create group';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  const updateWorkspaceGroup = useCallback(
    async (
      workspaceId: number,
      groupId: number,
      data: UpdateWorkspaceGroupRequest
    ): Promise<WorkspaceGroup> => {
      setError(null);

      try {
        const response = await workspaceService.updateWorkspaceGroup(workspaceId, groupId, data);
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Invalid server response');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update group';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  const deleteWorkspaceGroup = useCallback(
    async (workspaceId: number, groupId: number): Promise<void> => {
      setError(null);

      try {
        const response = await workspaceService.deleteWorkspaceGroup(workspaceId, groupId);
        if (!response.success) {
          throw new Error('Failed to delete group');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete group';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  // =====================================================================
  // GROUP MEMBER OPERATIONS
  // =====================================================================

  const loadGroupMembers = useCallback(
    async (workspaceId: number, groupId: number): Promise<WorkspaceGroupMember[]> => {
      setError(null);

      try {
        const response = await workspaceService.getGroupMembers(workspaceId, groupId);
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Invalid server response');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load group members';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  const addGroupMember = useCallback(
    async (
      workspaceId: number,
      groupId: number,
      data: AddGroupMemberRequest
    ): Promise<WorkspaceGroupMember> => {
      setError(null);

      try {
        const response = await workspaceService.addGroupMember(workspaceId, groupId, data);
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Invalid server response');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add group member';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  const removeGroupMember = useCallback(
    async (workspaceId: number, groupId: number, userId: number): Promise<void> => {
      setError(null);

      try {
        const response = await workspaceService.removeGroupMember(workspaceId, groupId, userId);
        if (!response.success) {
          throw new Error('Failed to remove group member');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to remove group member';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load workspaces on mount
  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  // Clear workspace data on logout (defense-in-depth security)
  useEffect(() => {
    if (!user) {
      // User logged out - clear all workspace state and localStorage
      setCurrentWorkspace(null);
      setWorkspaces([]);
      localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    }
  }, [user]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<WorkspaceContextType>(
    () => ({
      currentWorkspace,
      workspaces,
      isLoading,
      error,
      loadWorkspaces,
      createWorkspace,
      updateWorkspace,
      deleteWorkspace,
      setActiveWorkspace,
      switchWorkspace,
      loadWorkspaceMembers,
      addWorkspaceMember,
      updateWorkspaceMember,
      removeWorkspaceMember,
      loadWorkspaceGroups,
      createWorkspaceGroup,
      updateWorkspaceGroup,
      deleteWorkspaceGroup,
      loadGroupMembers,
      addGroupMember,
      removeGroupMember,
      clearError,
    }),
    [
      currentWorkspace,
      workspaces,
      isLoading,
      error,
      loadWorkspaces,
      createWorkspace,
      updateWorkspace,
      deleteWorkspace,
      setActiveWorkspace,
      switchWorkspace,
      loadWorkspaceMembers,
      addWorkspaceMember,
      updateWorkspaceMember,
      removeWorkspaceMember,
      loadWorkspaceGroups,
      createWorkspaceGroup,
      updateWorkspaceGroup,
      deleteWorkspaceGroup,
      loadGroupMembers,
      addGroupMember,
      removeGroupMember,
      clearError,
    ]
  );

  return <WorkspaceContext.Provider value={contextValue}>{children}</WorkspaceContext.Provider>;
};

/**
 * Custom hook to use the WorkspaceContext
 * Throws an error if used outside of WorkspaceProvider
 */
export const useWorkspace = (): WorkspaceContextType => {
  const context = useContext(WorkspaceContext);

  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }

  return context;
};
